import { inject } from '@angular/core';
import { CanDeactivateFn, UrlTree } from '@angular/router';
import { StorageService } from '../services/storage.service';

/**
 * Creates a guard that auto-clears storage when you leave a module.
 *
 * Think of it like cleaning up your workspace when you leave a room - it keeps things tidy but doesn't touch anything
 * while you're still there.
 *
 * **What it does:**
 *
 * - Keeps storage intact while navigating within the module (`/template/page1` → `/template/page2`)
 * - Clears storage when leaving for another module (`/template` → `/admin`)
 * - Survives page refreshes (no navigation = no cleanup)
 *
 * **Good for:**
 *
 * - Filters and search state that should persist during use
 * - Temporary form data that shouldn't leak between modules
 * - UI state like pagination and sorting
 *
 * @example
 *   // Basic - clear session storage when leaving template module
 *   canDeactivate: [createStorageCleanupGuard('template-mgmt', '/template')];
 *
 * @example
 *   // Use localStorage for persistent preferences
 *   canDeactivate: [createStorageCleanupGuard('user-prefs', '/settings', 'local')];
 *
 * @param prefix Storage key prefix (e.g., 'template-mgmt', 'admin-users')
 * @param modulePath Module path to watch (e.g., '/template', '/admin')
 * @param storageType 'session' (default) or 'local' storage
 * @returns Route guard function
 */
export function createStorageCleanupGuard(
  prefix: string,
  modulePath: string,
  storageType: 'session' | 'local' = 'session'
): CanDeactivateFn<unknown> {
  return (_component, _currentRoute, _currentState, nextState?) => {
    const storage = inject(StorageService);

    // If no next state, allow navigation (shouldn't happen)
    if (!nextState) {
      return true;
    }

    // Get the target URL
    const targetUrl = nextState instanceof UrlTree ? nextState.toString() : nextState.url;

    // Check if we're staying within the same module
    const stayingInModule = targetUrl.startsWith(modulePath);

    // Only clear storage if leaving the module
    if (!stayingInModule) {
      storage.setConfig({ type: storageType, prefix });
      storage.clear();
    }

    // Always allow navigation
    return true;
  };
}
